import {
  CRM_STATUS_CREATED,
  CRM_STATUS_FAILED,
  CRM_STATUS_LINKED,
  CRM_STATUS_SKIPPED,
  type CrmOutcome,
} from "@/v2-backoffice-client/crm/crm.constants"

export type CrmOutcomeCounts = {
  created: number
  linked: number
  skipped: number
  failed: number
}

/** Tally a batch of per-person HubSpot outcomes. */
export function countCrmOutcomes(outcomes: CrmOutcome[]): CrmOutcomeCounts {
  const counts: CrmOutcomeCounts = {
    created: 0,
    linked: 0,
    skipped: 0,
    failed: 0,
  }
  for (const outcome of outcomes) {
    switch (outcome.Status) {
      case CRM_STATUS_CREATED:
        counts.created++
        break
      case CRM_STATUS_LINKED:
        counts.linked++
        break
      case CRM_STATUS_FAILED:
        counts.failed++
        break
      case CRM_STATUS_SKIPPED:
        counts.skipped++
        break
    }
  }
  return counts
}

/**
 * One line summarising what happened in HubSpot for a batch — the part of
 * a "users created" / "invitations sent" toast that answers "and the CRM?".
 * Empty when there is nothing to say (no outcomes at all).
 */
export function describeCrmOutcomes(outcomes: CrmOutcome[], noun = "contact") {
  if (outcomes.length === 0) return ""
  const counts = countCrmOutcomes(outcomes)
  const parts: string[] = []
  if (counts.created > 0) {
    parts.push(`${counts.created} ${plural(noun, counts.created)} created`)
  }
  if (counts.linked > 0) {
    parts.push(`${counts.linked} linked to existing`)
  }
  if (counts.skipped > 0) {
    parts.push(`${counts.skipped} skipped`)
  }
  if (counts.failed > 0) {
    parts.push(`${counts.failed} failed`)
  }
  return `HubSpot: ${parts.join(" · ")}`
}

/** The one-line explanation of a single person's HubSpot outcome. */
export function describeCrmOutcome(outcome: CrmOutcome, noun = "contact") {
  switch (outcome.Status) {
    case CRM_STATUS_CREATED:
      return `HubSpot ${noun} created and linked`
    case CRM_STATUS_LINKED:
      return `Linked to the existing HubSpot ${noun}`
    case CRM_STATUS_FAILED:
      return `HubSpot step failed${outcome.Message ? `: ${outcome.Message}` : ""}`
    default:
      return outcome.Message
        ? `HubSpot: ${outcome.Message}`
        : `No HubSpot ${noun} linked`
  }
}

function plural(noun: string, count: number) {
  return count === 1 ? noun : `${noun}s`
}
